import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import {
  Navigate,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { Button, Icon, Link, Paper, Typography } from '@/ui';

import type {
  OnboardingReviewApiAnswer,
  OnboardingReviewApiPayload,
  OnboardingReviewApiSection,
} from '@/store/api/pulseApi';
import type { PulseReportAnswer, PulseReportSection } from '@/types';
import type { ReactNode } from 'react';

import { PendingDebriefAction } from '@/components/Insights/PulseReport';
import Section from '@/components/Insights/PulseReport/Section';
import {
  useGetOnboardingReviewQuery,
  useGetPulseReportQuery,
  useGetPulseReportsQuery,
  useMarkPulseReportDebriefedMutation,
} from '@/store/api/pulseApi';
import {
  mapPulseReportToPayload,
  toRangeBucket,
} from '@/utils/pulseReportAdapters';

const formatSubmittedAt = (submittedAt?: string) => {
  if (!submittedAt) {
    return 'Unknown date';
  }

  const parsed = new Date(submittedAt);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown date';
  }

  return parsed.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatAnswerText = (answer: OnboardingReviewApiAnswer) => {
  if (Array.isArray(answer.value)) {
    return answer.value.join(', ');
  }

  if (typeof answer.value === 'boolean') {
    return answer.value ? 'Yes' : 'No';
  }

  return String(answer.value);
};

const toPulseAnswer = (
  answer: OnboardingReviewApiAnswer,
  order: number,
  sectionKey: OnboardingReviewApiSection['key'],
): PulseReportAnswer => {
  if (typeof answer.value === 'number' && sectionKey === 'baseline') {
    const numericValue = Math.max(0, Math.min(10, answer.value));

    return {
      bucket: toRangeBucket(numericValue),
      id: answer.id,
      kind: 'range10',
      max: 10,
      min: 0,
      order,
      prompt: answer.prompt,
      questionKey: answer.questionKey,
      value: numericValue,
    };
  }

  if (Array.isArray(answer.value)) {
    return {
      id: answer.id,
      kind: 'multi_select',
      order,
      prompt: answer.prompt,
      questionKey: answer.questionKey,
      selections: answer.value,
    };
  }

  return {
    author: {
      name: 'Member',
    },
    id: answer.id,
    kind: 'freeform',
    order,
    prompt: answer.prompt,
    questionKey: answer.questionKey,
    text: formatAnswerText(answer),
  };
};

const orderReviewSections = (sections: OnboardingReviewApiSection[]) =>
  [...sections].sort((left, right) => {
    const leftIsMatching = left.key === 'matching';
    const rightIsMatching = right.key === 'matching';

    if (leftIsMatching !== rightIsMatching) {
      return leftIsMatching ? 1 : -1;
    }

    return left.order - right.order;
  });

const toPulseSection = (
  section: OnboardingReviewApiSection,
): PulseReportSection => ({
  answers: section.answers.map((answer, index) =>
    toPulseAnswer(answer, index + 1, section.key),
  ),
  key: section.key as PulseReportSection['key'],
  order: section.order,
  title: section.title,
});

const pulseSectionsFromReview = (
  review: OnboardingReviewApiPayload,
): PulseReportSection[] => {
  if (review.pulseSections?.length) {
    return mapPulseReportToPayload({
      member: { fullName: 'Member' },
      memberId: '',
      reportId: review.pulseReportId,
      sections: review.pulseSections,
      sourceVersion: review.source.schemaVersion,
      submittedAt: review.submittedAt,
    }).sections.filter((section) => section.answers.length > 0);
  }

  return orderReviewSections(review.sections)
    .filter((section) => section.key !== 'matching')
    .map(toPulseSection);
};

const matchingSectionsFromReview = (review: OnboardingReviewApiPayload) =>
  orderReviewSections(review.sections)
    .filter((section) => section.key === 'matching')
    .map(toPulseSection);

type OnboardingReviewStatus =
  'error' | 'loading' | 'not_found' | 'ready' | 'unauthorized';

interface MemberOnboardingReviewPureProps {
  aside?: ReactNode;
  backLabel?: string;
  backPath: string;
  onBack: () => void;
  onRetry?: () => void;
  review?: OnboardingReviewApiPayload;
  status: OnboardingReviewStatus;
}

const MemberOnboardingReviewPure = ({
  aside,
  backLabel = 'Insights',
  backPath,
  onBack,
  onRetry,
  review,
  status,
}: MemberOnboardingReviewPureProps) => {
  if (status === 'loading') {
    return (
      <div className="p-4 lg:p-6">
        <div className="mx-auto w-full max-w-280">
          <Typography variant="h4">Loading survey review…</Typography>
        </div>
      </div>
    );
  }

  if (status !== 'ready' || !review) {
    const isError = status === 'error';
    const isUnauthorized = status === 'unauthorized';
    const title = (() => {
      if (isUnauthorized) {
        return 'Survey review unavailable';
      }
      if (isError) {
        return 'We could not load your survey review';
      }
      return 'Survey review not found';
    })();
    const body = (() => {
      if (isUnauthorized) {
        return 'You do not have access to this survey review.';
      }
      if (isError) {
        return 'Something went wrong while loading your Coach Matching & Baseline Survey review. Please try again.';
      }
      return 'We could not find this Coach Matching & Baseline Survey review.';
    })();

    return (
      <div className="p-4 lg:p-6">
        <Paper className="mx-auto w-full max-w-280 space-y-4 rounded-2xl p-6 lg:p-8">
          <Typography variant="h4">{title}</Typography>
          <Typography className="text-cement-400">{body}</Typography>
          {isError && onRetry && (
            <Button variant="contained" onClick={onRetry}>
              Try again
            </Button>
          )}
          <Button variant="text" onClick={onBack}>
            Back to {backLabel}
          </Button>
        </Paper>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6" data-testid="onboarding-review-page">
      <div className="mx-auto flex w-full max-w-280 flex-col gap-4">
        <Link
          className="flex no-underline"
          color="text.primary"
          component={RouterLink}
          to={backPath}
          onClick={(event) => {
            event.preventDefault();
            onBack();
          }}
        >
          <Icon>chevron_left</Icon>
          {backLabel}
        </Link>
        <div
          className={clsx('flex flex-col gap-4', {
            'xl:grid xl:grid-cols-[minmax(0,1fr)_10rem] xl:items-start': aside,
          })}
          data-testid="onboarding-review-layout"
        >
          <div className="flex flex-col gap-6">
            <section className="scroll-mt-6" id="pulse">
              <Paper className="rounded-2xl p-6 lg:p-8" elevation={3}>
                <div className="mb-4 flex flex-col gap-1 px-0 md:px-6">
                  <Typography className="text-cement-600" variant="h4">
                    Pulse Check
                  </Typography>
                  <Typography className="text-cement-400" variant="body2">
                    Submitted {formatSubmittedAt(review.submittedAt)}
                  </Typography>
                </div>
                <div className="flex flex-col gap-4">
                  {pulseSectionsFromReview(review).map((section) => (
                    <Section key={section.key} section={section} />
                  ))}
                </div>
              </Paper>
            </section>
            {matchingSectionsFromReview(review).map((section) => (
              <section key={section.key} className="scroll-mt-6" id="matching">
                <Paper className="rounded-2xl p-6 lg:p-8" elevation={3}>
                  <div className="mb-4 flex flex-col gap-1 px-0 md:px-6">
                    <Typography className="text-cement-600" variant="h4">
                      Coach Matching
                    </Typography>
                    <Typography className="text-cement-400" variant="body2">
                      Submitted {formatSubmittedAt(review.submittedAt)}
                    </Typography>
                  </div>
                  <Section section={section} />
                </Paper>
              </section>
            ))}
          </div>
          {aside ? <div className="xl:sticky xl:top-6">{aside}</div> : null}
        </div>
      </div>
    </div>
  );
};

interface OnboardingReviewRouteProps {
  backLabel?: string;
  backPath: string;
  debriefEnabled?: boolean;
}

const OnboardingReviewRoute = ({
  backLabel = 'Insights',
  backPath,
  debriefEnabled = false,
}: OnboardingReviewRouteProps) => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { reportId } = useParams<{ reportId?: string }>();
  const { currentData, data, error, isFetching, isLoading, refetch } =
    useGetOnboardingReviewQuery(reportId || '', { skip: !reportId });
  const { data: reportData, refetch: refetchReport } = useGetPulseReportQuery(
    reportId || '',
    { skip: !reportId || !debriefEnabled },
  );
  const [debriefError, setDebriefError] = useState<string>();
  const [markDebriefed, { isLoading: isMarkingDebriefed }] =
    useMarkPulseReportDebriefedMutation();

  useEffect(() => {
    setDebriefError(undefined);
  }, [reportId]);

  const status = (error as { status?: number } | undefined)?.status;
  const reviewStatus: OnboardingReviewStatus = (() => {
    if (isLoading || (isFetching && !currentData?.review)) {
      return 'loading';
    }
    if (!reportId || status === 404) {
      return 'not_found';
    }
    if (status === 403) {
      return 'unauthorized';
    }
    if (error || !data?.review) {
      return 'error';
    }
    return 'ready';
  })();

  useEffect(() => {
    if (reviewStatus !== 'ready' || !hash) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      document
        .getElementById(decodeURIComponent(hash.slice(1)))
        ?.scrollIntoView({ block: 'start' });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [hash, reviewStatus]);

  const debrief = debriefEnabled ? reportData?.report.debrief : undefined;
  const showPendingDebriefAction =
    reviewStatus === 'ready' &&
    debrief?.status === 'pending' &&
    debrief.canMarkDebriefed;

  const handleMarkDebriefed = async () => {
    if (!reportId) {
      return;
    }

    setDebriefError(undefined);

    try {
      await markDebriefed(reportId).unwrap();
    } catch {
      setDebriefError('Unable to mark this pulse report as debriefed.');
      refetchReport();
    }
  };

  return (
    <MemberOnboardingReviewPure
      aside={
        showPendingDebriefAction ? (
          <PendingDebriefAction
            debriefError={debriefError}
            isMarkingDebriefed={isMarkingDebriefed}
            onMarkDebriefed={handleMarkDebriefed}
          />
        ) : undefined
      }
      backLabel={backLabel}
      backPath={backPath}
      review={data?.review}
      status={reviewStatus}
      onBack={() => navigate(backPath)}
      onRetry={() => refetch()}
    />
  );
};

const MemberOnboardingReview = () => (
  <OnboardingReviewRoute backPath="/m/insights" />
);

const CoachMemberOnboardingReview = () => {
  const { id } = useParams<{ id?: string }>();

  return (
    <OnboardingReviewRoute
      backPath={`/coach/members/${id}/insights`}
      debriefEnabled
    />
  );
};

// MEN-3576: the coach's series booking email links here, because only Rails knows which report is
// the member's baseline. Forward to the latest baseline review, or to the member's Insights page
// when there is none, so the coach can see the survey is still incomplete.
const CoachMemberLatestOnboardingReview = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const insightsPath = `/coach/members/${id}/insights`;
  const { data, isError, isSuccess } = useGetPulseReportsQuery(
    {
      formType: 'member_onboarding',
      memberId: id || '',
      onboardingReviewAvailable: true,
      page: 1,
      per: 1,
    },
    { skip: !id },
  );

  const baselineReport = data?.reports.find(
    (report) => report.isOnboardingBaseline === true,
  );

  if (baselineReport) {
    return (
      <Navigate
        to={`/coach/members/${id}/onboarding-review/${baselineReport.reportId}`}
        replace
      />
    );
  }

  if (!id || isError || isSuccess) {
    return <Navigate to={insightsPath} replace />;
  }

  return (
    <MemberOnboardingReviewPure
      backPath={insightsPath}
      status="loading"
      onBack={() => navigate(insightsPath)}
    />
  );
};

export {
  CoachMemberLatestOnboardingReview,
  CoachMemberOnboardingReview,
  MemberOnboardingReviewPure,
};
export type { MemberOnboardingReviewPureProps, OnboardingReviewStatus };
export default MemberOnboardingReview;
